// E-commerce Admin Dashboard Navigation with RBAC
import type { VerticalMenuDataType } from '@/types/menuTypes'
import type { getDictionary } from '@/utils/getDictionary'
import { UserRole } from '@/types/rbac'

const ecommerceMenuData = (dictionary: Awaited<ReturnType<typeof getDictionary>>): VerticalMenuDataType[] => [
  // Dashboard
  {
    label: dictionary['navigation'].dashboard,
    icon: 'tabler-dashboard',
    href: '/dashboard',
    requiredPermissions: ['view-dashboard']
  },

  // Marketing Section
  {
    label: dictionary['navigation'].marketing,
    isSection: true,
    requiredPermissions: ['view-slides', 'view-reels'],
    children: [
      {
        label: dictionary['navigation'].marketing,
        icon: 'tabler-speakerphone',
        children: [
          {
            label: dictionary['navigation'].slidesShop,
            icon: 'tabler-building-store',
            href: '/marketing/slides',
            requiredPermissions: ['view-slides']
          },
          {
            label: dictionary['navigation'].slidesTags,
            icon: 'tabler-tags',
            href: '/marketing/slides-tags',
            requiredPermissions: ['view-slides']
          },
          {
            label: dictionary['navigation'].slidesCategories,
            icon: 'tabler-category',
            href: '/marketing/slides-categories',
            requiredPermissions: ['view-slides']
          },
          {
            label: dictionary['navigation'].slidesMiniCategories,
            icon: 'tabler-layout-grid',
            href: '/marketing/slides-mini-categories',
            requiredPermissions: ['view-slides']
          },
          {
            label: dictionary['navigation'].slidesBrands,
            icon: 'tabler-brand-stackshare',
            href: '/marketing/slides-brands',
            requiredPermissions: ['view-slides']
          },
          {
            label: dictionary['navigation'].reels,
            icon: 'tabler-movie',
            href: '/marketing/reels',
            requiredPermissions: ['view-reels']
          }
        ]
      }
    ]
  },

  // Catalog Section
  {
    label: dictionary['navigation'].catalog,
    isSection: true,
    requiredPermissions: ['view-products', 'view-categories', 'view-brands', 'view-colors', 'view-sizes'],
    children: [
      {
        label: dictionary['navigation'].catalog,
        icon: 'tabler-package',
        children: [
          {
            label: dictionary['navigation'].products,
            icon: 'tabler-circle',
            href: '/catalog/products',
            requiredPermissions: ['view-products']
          },
          {
            label: dictionary['navigation'].category,
            icon: 'tabler-circle',
            href: '/catalog/categories',
            requiredPermissions: ['view-categories']
          },
          // Subcategories are managed inline from the Categories page — the
          // /catalog/subcategories route still exists, it's just not in the menu
          // {
          //   label: dictionary['navigation'].subcategories,
          //   icon: 'tabler-circle',
          //   href: '/catalog/subcategories',
          //   requiredPermissions: ['view-sub-categories']
          // },
          {
            label: dictionary['navigation'].brands,
            icon: 'tabler-circle',
            href: '/catalog/brands',
            requiredPermissions: ['view-brands']
          },

          // {
          //   label: dictionary['navigation'].attributes,
          //   icon: 'tabler-circle',
          //   href: '/catalog/attributes',
          //   requiredPermissions: ['view-colors', 'view-sizes']
          // },
          {
            label: dictionary['navigation'].reviews,
            icon: 'tabler-circle',
            href: '/catalog/reviews',
            requiredPermissions: ['view-products'] // Defaulting to products view permission for reviews
          }

          // {
          //   label: dictionary['navigation'].offers,
          //   icon: 'tabler-circle',
          //   href: '/catalog/offers',
          //   requiredPermissions: ['view-offers']
          // }
        ]
      }
    ]
  },

  // Orders Section
  {
    label: dictionary['navigation'].orders,
    isSection: true,
    requiredPermissions: ['view-orders'],
    children: [
      {
        label: dictionary['navigation'].orders,
        icon: 'tabler-shopping-cart',
        href: '/orders',
        requiredPermissions: ['view-orders']
      }
    ]
  },

  // Shipments Section
  {
    label: dictionary['navigation'].shipments,
    isSection: true,
    requiredPermissions: ['view-orders'],
    children: [
      {
        label: dictionary['navigation'].senderSettings,
        icon: 'tabler-address-book',
        href: '/shipments/sender-settings',
        requiredPermissions: ['view-settings']
      },
      {
        label: dictionary['navigation'].shipments,
        icon: 'tabler-package-export',
        href: '/shipments',
        requiredPermissions: ['view-orders']
      },
      {
        label: dictionary['navigation'].shippingCompanies,
        icon: 'tabler-truck',
        href: '/shipments/companies',
        requiredPermissions: ['view-settings']
      },
      {
        label: dictionary['navigation'].localTeam,
        icon: 'tabler-truck-delivery',
        href: '/local-team',
        requiredPermissions: ['view-orders']
      }
    ]
  },

  // Payments Section
  {
    label: dictionary['navigation'].payments,
    isSection: true,
    requiredPermissions: ['view-orders'],
    children: [
      {
        label: dictionary['navigation'].payments,
        icon: 'tabler-credit-card',
        children: [
          {
            label: dictionary['navigation'].paymentOptions,
            icon: 'tabler-receipt',
            href: '/payments/options',
            requiredPermissions: ['view-settings']
          },
          {
            label: dictionary['navigation'].transactions,
            icon: 'tabler-circle',
            href: '/payments/transactions',
            requiredPermissions: ['view-orders']
          },
          {
            label: dictionary['navigation'].paymentMethods,
            icon: 'tabler-circle',
            href: '/payments/methods',
            requiredPermissions: ['view-settings']
          },
          {
            label: dictionary['navigation'].webhooks,
            icon: 'tabler-webhook',
            href: '/payments/webhooks',
            requiredPermissions: ['view-settings']
          }
        ]
      }
    ]
  },

  // {
  //   label: dictionary['navigation'].paymentsAndTaxes,
  //   isSection: true,
  //   requiredPermissions: ['view-wallets'],
  //   children: [
  //     {
  //       label: dictionary['navigation'].paymentsAndTaxes,
  //       icon: 'tabler-credit-card',
  //       children: [
  //         {
  //           label: dictionary['navigation'].paymentMethods,
  //           icon: 'tabler-circle',
  //           href: '/payments/methods',
  //           requiredPermissions: ['view-settings']
  //         },
  //         {
  //           label: dictionary['navigation'].freeShippingThreshold,
  //           icon: 'tabler-circle',
  //           href: '/payments/shipping-threshold',
  //           requiredPermissions: ['view-settings']
  //         },
  //         {
  //           label: dictionary['navigation'].walletManagement,
  //           icon: 'tabler-circle',
  //           href: '/payments/wallet',
  //           requiredPermissions: ['view-wallets']
  //         },
  //         {
  //           label: dictionary['navigation'].webhooks,
  //           icon: 'tabler-circle',
  //           href: '/payments/webhooks',
  //           requiredPermissions: ['view-settings']
  //         }
  //       ]
  //     }
  //   ]
  // },

  // Customer Support Section — Customers, Tickets, Promo Codes & Campaigns
  {
    label: dictionary['navigation'].customerSupport,
    isSection: true,
    requiredPermissions: [
      'view-tickets',
      'view-users',
      'manage-user-wallets',
      'view-push-campaigns',
      'view-email-campaigns'
    ],
    children: [
      {
        label: dictionary['navigation'].customers,
        icon: 'tabler-users',
        href: '/customers',
        requiredPermissions: ['view-users']
      },
      {
        label: dictionary['navigation'].tickets,
        icon: 'tabler-ticket',
        href: '/tickets',
        requiredPermissions: ['view-tickets']
      },
      {
        label: dictionary['navigation'].promoCodes,
        icon: 'tabler-discount',
        href: '/promo-codes',
        requiredPermissions: ['manage-user-wallets']
      },

      // Campaign routes intentionally stay under /marketing/* (History links depend on them)
      {
        label: dictionary['navigation'].pushCampaigns,
        icon: 'tabler-bell-ringing',
        href: '/marketing/push-campaigns',
        requiredPermissions: ['view-push-campaigns']
      },
      {
        label: dictionary['navigation'].emailCampaigns,
        icon: 'tabler-mail-forward',
        href: '/marketing/email-campaigns',
        requiredPermissions: ['view-email-campaigns']
      }
    ]
  },

  // Users & Permissions Section
  {
    label: dictionary['navigation'].usersAndPermissions,
    isSection: true,
    requiredPermissions: ['view-users', 'view-roles', 'view-permissions', 'view-permission-categories'],
    children: [
      {
        label: dictionary['navigation'].usersAndRoles,
        icon: 'tabler-users',
        href: '/users',
        requiredPermissions: ['view-users']
      },
      {
        label: dictionary['navigation'].roles,
        icon: 'tabler-lock',
        href: '/users/roles',
        requiredPermissions: ['view-roles']
      },
      {
        label: dictionary['navigation'].permissions,
        icon: 'tabler-key',
        href: '/users/permissions',
        requiredPermissions: ['view-permissions']
      },
      {
        label: dictionary['navigation'].permissionCategories,
        icon: 'tabler-category',
        href: '/users/permission-categories',
        requiredPermissions: ['view-permission-categories']
      }
    ]
  },

  // OTP & Auth Section
  {
    label: dictionary['navigation'].otp,
    isSection: true,
    requiredPermissions: ['view-settings'],
    children: [
      {
        label: dictionary['navigation'].otp,
        icon: 'tabler-shield-lock',
        href: '/otp/sms',
        requiredPermissions: ['view-settings']
      }

      // {
      //   label: dictionary['navigation'].emailAuth,
      //   icon: 'tabler-mail',
      //   href: '/otp/email',
      //   requiredPermissions: ['view-settings']
      // }
    ]
  },

  // Systems & Analytics Section
  {
    label: dictionary['navigation'].systemsAndAnalytics,
    isSection: true,
    requiredPermissions: ['view-system-alerts', 'view-reports', 'edit-settings', 'view-activity-log', 'view-settings'],
    children: [
      {
        label: dictionary['navigation'].systemAlerts,
        icon: 'tabler-alert-triangle',
        href: '/system-alerts',
        requiredPermissions: ['view-system-alerts']
      },
      {
        label: dictionary['navigation'].activityLog,
        icon: 'tabler-history',
        href: '/activity-log',
        requiredPermissions: ['view-activity-log']
      },
      {
        label: dictionary['navigation'].reports,
        icon: 'tabler-chart-bar',
        href: '/reports',
        requiredPermissions: ['view-reports']
      },
      {
        label: dictionary['navigation'].maintenanceMode,
        icon: 'tabler-tool',
        href: '/maintenance-mode',
        requiredPermissions: ['edit-settings']
      },
      {
        label: dictionary['navigation'].smtpSettings,
        icon: 'tabler-mail-cog',
        href: '/settings/smtp',
        requiredPermissions: ['view-settings']
      }
    ]
  },

  // CMS Section
  {
    label: dictionary['navigation'].contentManagement,
    isSection: true,
    requiredPermissions: ['view-settings'],
    children: [
      {
        label: dictionary['navigation'].cms,
        icon: 'tabler-file-text',
        children: [
          {
            label: dictionary['navigation'].termsOfService,
            icon: 'tabler-circle',
            href: '/cms/terms',
            requiredPermissions: ['view-settings']
          },
          {
            label: dictionary['navigation'].privacyPolicy,
            icon: 'tabler-circle',
            href: '/cms/privacy',
            requiredPermissions: ['view-settings']
          },
          {
            label: dictionary['navigation'].helpAndFaq,
            icon: 'tabler-circle',
            href: '/cms/help',
            requiredPermissions: ['view-settings']
          },
          {
            label: dictionary['navigation'].contactUs,
            icon: 'tabler-circle',
            href: '/cms/contact-us',
            requiredPermissions: ['view-settings']
          }
        ]
      }
    ]
  },

  // Remaining settings pages — temporarily disabled, will be used later
  // {
  //   label: dictionary['navigation'].settings,
  //   isSection: true,
  //   requiredPermissions: ['view-settings'],
  //   children: [
  //     {
  //       label: dictionary['navigation'].settings,
  //       icon: 'tabler-settings',
  //       children: [
  //         {
  //           label: dictionary['navigation'].settings,
  //           icon: 'tabler-circle',
  //           href: '/settings',
  //           requiredPermissions: ['view-settings']
  //         },
  //         {
  //           label: dictionary['navigation'].colors,
  //           icon: 'tabler-circle',
  //           href: '/settings/colors',
  //           requiredPermissions: ['view-colors']
  //         },
  //         {
  //           label: dictionary['navigation'].sizes,
  //           icon: 'tabler-circle',
  //           href: '/settings/sizes',
  //           requiredPermissions: ['view-sizes']
  //         }
  //       ]
  //     }
  //   ]
  // },
]

export default ecommerceMenuData
